import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

import { ServiceApi } from "../../Service/ServiceApi";
import styles from "./FilterBar.module.css";

const FilterBar = ({
  filterCategories,
  filterOptions,
  selectedFilters,
  onOpen,
  setSelectedFilters,
  updateResults,
  categoryMap,
  fetchJobs,
}) => {
  const history = useHistory();
  const navigate = useHistory(); // keeping this as you had it for Reset
  const dropdownRef = useRef(null);

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Jobs");

  const [companySearch, setCompanySearch] = useState("");
  const [industrySearch, setIndustrySearch] = useState("");
  const [jobFunctionSearch, setJobFunctionSearch] = useState("");

  // 🔽 NEW: how many filterCategories to show on desktop
  const [maxVisibleFilters, setMaxVisibleFilters] = useState(
    filterCategories.length
  );

  const token = localStorage.getItem("token");
  const id = useParams();

  const getSearchState = useCallback(
    (filterName) => {
      switch (filterName) {
        case "Company":
          return [companySearch, setCompanySearch];
        case "Industry":
          return [industrySearch, setIndustrySearch];
        case "Job function":
          return [jobFunctionSearch, setJobFunctionSearch];
        default:
          return ["", () => {}];
      }
    },
    [companySearch, industrySearch, jobFunctionSearch]
  );

  const togglePageDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (option) => {
    setSelectedOption(option);
    if (option === "People") {
      history.push("/people");
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const result = await ServiceApi.fetchCompany(token);
        if (result?.data?.companies) {
          filterOptions["company"].options = result?.data?.companies.map(
            (companyName) => {
              return { value: companyName, label: companyName };
            }
          );
        }
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    };

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    fetchCompany();
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [token, filterOptions]);

  const toggleDropdown = (filterName) => {
    setActiveDropdown(activeDropdown === filterName ? null : filterName);
  };

  const getSelectionType = useCallback(
    (filterName) => {
      const apiCategory = Object.keys(filterOptions).find(
        (category) =>
          category.toLowerCase() === filterName.toLowerCase() ||
          filterOptions[category]?.label?.toLowerCase() ===
            filterName.toLowerCase()
      );
      return filterOptions[apiCategory]?.type?.toLowerCase() ?? "checkbox";
    },
    [filterOptions]
  );

  const handleSelection = (filterName, option) => {
    const apiKey = categoryMap[filterName] ?? filterName;
    const filterType = getSelectionType(filterName);

    if (filterType === "checkbox") {
      setSelectedFilters((prev) => {
        const updated = { ...prev };
        updated[apiKey] = updated[apiKey] ? [...updated[apiKey]] : [];
        if (updated[apiKey].includes(option)) {
          updated[apiKey] = updated[apiKey].filter((item) => item !== option);
        } else {
          updated[apiKey].push(option);
        }
        return updated;
      });
    } else {
      setSelectedFilters((prev) => ({ ...prev, [apiKey]: option }));
    }
  };

  const handleReset = (filterName) => {
    const apiKey = categoryMap[filterName] ?? filterName;
    setSelectedFilters((prev) => {
      const updated = { ...prev };
      delete updated[apiKey];
      if (Object.keys(updated).length > 0) {
        setTimeout(() => updateResults(updated), 0);
      } else {
        fetchJobs();
      }
      return updated;
    });
  };

  const getDropdownOptions = useCallback(
    (filterName) => {
      const apiCategory = Object.keys(filterOptions).find(
        (category) =>
          category.toLowerCase() === filterName.toLowerCase() ||
          filterOptions[category]?.label?.toLowerCase() ===
            filterName.toLowerCase()
      );

      if (apiCategory && filterOptions[apiCategory]?.options) {
        const [searchValue] = getSearchState(filterName);
        let allOptions = filterOptions[apiCategory].options;
        let options = [...allOptions];

        if (searchValue.trim() !== "") {
          options = options.filter((opt) =>
            opt.label.toLowerCase().includes(searchValue.toLowerCase())
          );
        }

        if (
          searchValue.trim() === "" &&
          ["Industry", "Job function", "Company"].includes(filterName)
        ) {
          options = options.slice(0, 5);
        }

        return options;
      }
      return ["No options available"];
    },
    [filterOptions, getSearchState]
  );

  // 🔽 NEW: dynamic hide-last-filters logic for desktop
  useEffect(() => {
    const updateVisibleFilters = () => {
      const width = window.innerWidth;

      // On mobile, show all filters and rely on horizontal scroll
      if (width < 800) {
        setMaxVisibleFilters(filterCategories.length);
        return;
      }

      let max = filterCategories.length;

      // tweak these breakpoints as needed to match your actual layout
      if (width < 1400) max = filterCategories.length - 1;
      if (width < 1250) max = filterCategories.length - 2;
      if (width < 1100) max = filterCategories.length - 3;
      if (width < 950) max = filterCategories.length - 4;

      max = Math.max(0, Math.min(filterCategories.length, max));
      setMaxVisibleFilters(max);
    };

    updateVisibleFilters();
    window.addEventListener("resize", updateVisibleFilters);

    return () => {
      window.removeEventListener("resize", updateVisibleFilters);
    };
  }, [filterCategories.length]);

  return (
    <div className={styles.filterBarContainer}>
      <div className={styles.filtersWrapper} ref={dropdownRef}>
        {/* Jobs / People / All dropdown */}
        <div className={styles.filterItem}>
          <button
            className={`${styles.filterButton} ${styles.active}`}
            onClick={togglePageDropdown}
          >
            {selectedOption}
            <span className={styles.dropdown_Icon}>▼</span>
          </button>
          {isOpen && (
            <div className={styles.dropdownMenu}>
              {["All", "Jobs", "People"].map((option, index) => (
                <div
                  key={index}
                  className={styles.dropdownItem}
                  onClick={() => handleSelect(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.horizontalDivier}></div>

        {/* Filters (limited by maxVisibleFilters on desktop) */}
        {filterCategories.slice(0, maxVisibleFilters).map((filterName) => {
          const apiKey = categoryMap[filterName] ?? filterName;
          const hasSelection = Array.isArray(selectedFilters[apiKey])
            ? selectedFilters[apiKey]?.length > 0
            : Boolean(selectedFilters[apiKey]);
          const dropdownOptions = getDropdownOptions(filterName);
          const selectionType = getSelectionType(filterName);
          const [searchValue, setSearchValue] = getSearchState(filterName);
          const isSearchable = ["Company", "Industry", "Job function"].includes(
            filterName
          );

          return (
            <div className={styles.filterItem} key={filterName}>
              <button
                className={`${styles.filterButton} ${
                  activeDropdown === filterName ? styles.active : ""
                } ${hasSelection ? styles.selected : ""}`}
                onClick={() => toggleDropdown(filterName)}
              >
                {filterName}
                {hasSelection && (
                  <span className={styles.selectedCount}>
                    {Array.isArray(selectedFilters[apiKey])
                      ? selectedFilters[apiKey].length
                      : 1}
                  </span>
                )}
                <span
                  className={`${styles.Dropdown_Icons} ${
                    activeDropdown === filterName || hasSelection
                      ? styles.activeIcon
                      : ""
                  }`}
                >
                  ▼
                </span>
              </button>

              {activeDropdown === filterName && (
                <div className={styles.dropdownMenu}>
                  {isSearchable && (
                    <input
                      type="text"
                      placeholder={`Add a ${filterName.toLowerCase()}`}
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      className={styles.searchInput}
                    />
                  )}

                  {dropdownOptions.map((opt, index) => {
                    // when there are no options, opt is string "No options available"
                    const optionValue =
                      typeof opt === "string" ? opt : opt.value;
                    const optionLabel =
                      typeof opt === "string" ? opt : opt.label;

                    const isSelected = Array.isArray(selectedFilters[apiKey])
                      ? selectedFilters[apiKey]?.includes(optionValue)
                      : selectedFilters[apiKey] === optionValue;

                    return (
                      <div
                        key={index}
                        className={`${styles.dropdownItem} ${
                          isSelected ? styles.selected : ""
                        }`}
                        onClick={() =>
                          optionValue !== "No options available" &&
                          handleSelection(filterName, optionValue)
                        }
                      >
                        {selectionType === "checkbox" &&
                          optionValue !== "No options available" && (
                            <>
                              {isSelected ? (
                                <CheckBoxIcon sx={{ mr: 1 }} />
                              ) : (
                                <CheckBoxOutlineBlankIcon sx={{ mr: 1 }} />
                              )}
                            </>
                          )}

                        {selectionType === "radio" &&
                          optionValue !== "No options available" && (
                            <>
                              {isSelected ? (
                                <TripOriginIcon sx={{ mr: 1 }} />
                              ) : (
                                <PanoramaFishEyeIcon sx={{ mr: 1 }} />
                              )}
                            </>
                          )}

                        {optionLabel}
                      </div>
                    );
                  })}

                  <div className={styles.divider}></div>
                  <div className={styles.menuFooter}>
                    <button
                      className={styles.resetButton}
                      onClick={() => {
                        handleReset(filterName);
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className={styles.menuFooterBtn}
                      onClick={() => {
                        updateResults(selectedFilters);
                        setActiveDropdown(null);
                      }}
                    >
                      Show results
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div className={styles.horizontalDivier}></div>

        {/* All Filters + Reset */}
        <div className={styles.filterItem}>
          <button onClick={onOpen} className={styles.filterButton}>
            All Filters
          </button>
          {Object.keys(selectedFilters).length > 0 && (
            <button
              className={styles.resetButton}
              onClick={() => {
                if (id) {
                  navigate.push("/jobs", { replace: true });
                }
                setSelectedFilters({});
                fetchJobs();
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
